import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MarketAdminPanel } from "@/components/market/MarketAdminPanel";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Gear, CurrencyDollar, Users, MegaphoneSimple, ChartLine, Trash, CheckCircle, XCircle, Eye, Bank, Flag, Prohibit, Envelope, Crown, PaperPlaneTilt, WhatsappLogo, Phone, VideoCamera, Briefcase, GraduationCap, Coins, ArrowUp, ArrowDown, Gavel } from "@phosphor-icons/react";
import FileUpload from "@/components/FileUpload";

function StatsPanel() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data));
  }, []);
  if (!stats) return <div className="text-white/60">Loading…</div>;
  const cards = [
    { label: "Users", value: stats.total_users },
    { label: "Posts (total)", value: stats.total_posts },
    { label: "Live posts", value: stats.live_posts },
    { label: "Pending payment", value: stats.pending_payments },
    { label: "Pending approval", value: stats.pending_approvals },
    { label: "Ads (active)", value: `${stats.active_ads}/${stats.total_ads}` },
    { label: "Total WA clicks", value: stats.total_clicks },
    { label: "Pending reports", value: stats.pending_reports || 0 },
    { label: "Pending withdrawals", value: stats.pending_withdrawals || 0 },
    { label: "Pending appeals", value: stats.pending_appeals || 0 },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {cards.map((c) => (
        <Card key={c.label} className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-widest text-white/50 font-bold">{c.label}</p>
            <p className="font-heading text-3xl font-black mt-1">{c.value}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: parseFloat(v) || 0 }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      // int fields
      ["auto_hide_reports", "upload_point_cost", "like_point_cost", "comment_point_cost",
       "save_point_cost", "share_point_cost", "sound_point_cost", "download_point_cost"].forEach((k) => {
        if (payload[k] !== undefined) payload[k] = parseInt(payload[k], 10) || 0;
      });
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-white/60">Loading…</div>;

  const fields = [
    { k: "point_price", label: "USD per point" },
    { k: "upload_point_cost", label: "Upload cost (points)" },
    { k: "like_point_cost", label: "Credits per like" },
    { k: "comment_point_cost", label: "Credits per comment" },
    { k: "save_point_cost", label: "Credits per save" },
    { k: "share_point_cost", label: "Credits per share" },
    { k: "sound_point_cost", label: "Credits per sound reuse" },
    { k: "download_point_cost", label: "Credits per download" },
    { k: "creator_share_pct", label: "Creator share % (of points spent)" },
    { k: "publish_fee", label: "[Legacy] Publish fee USD" },
    { k: "like_reward", label: "[Legacy] Reward per like USD" },
    { k: "view_reward", label: "USD earned per view (creator)" },
    { k: "comment_reward", label: "[Legacy] Reward per comment USD" },
    { k: "share_reward", label: "[Legacy] Reward per share USD" },
    { k: "follow_reward", label: "Reward per new follower (USD)" },
    { k: "default_click_cost", label: "Default WhatsApp click cost USD" },
    { k: "daily_login_bonus", label: "[Legacy] Daily login bonus" },
    { k: "streak_7day_bonus", label: "[Legacy] 7-day streak bonus" },
    { k: "min_withdrawal", label: "Minimum withdrawal USD" },
    { k: "auto_hide_reports", label: "Auto-hide after N reports" },
    { k: "weekly_prize_pct", label: "Weekly prize pool % of publish fees" },
  ];

  return (
    <Card className="bg-[#121212] border-white/10 max-w-2xl">
      <CardHeader>
        <CardTitle className="font-heading">Platform rates (USD)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200">
          Feed viewing Credits, Jobs/Partners/School access fees are now managed in the <b>Credits economy</b> tab.
        </div>
        {fields.map((f) => (
          <div key={f.k} className="grid grid-cols-2 items-center gap-4">
            <label className="text-sm text-white/70">{f.label}</label>
            <Input
              data-testid={`settings-${f.k}`}
              type="number"
              step="0.01"
              value={settings[f.k]}
              onChange={(e) => update(f.k, e.target.value)}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>
        ))}
        <Button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full bg-blue-600 hover:bg-blue-700"
        >
          Save settings
        </Button>
      </CardContent>
    </Card>
  );
}

function VideoMaintenancePanel() {
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [queueSummary, setQueueSummary] = useState(null);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: q } = await api.get("/admin/maintenance/upload-sessions/summary");
      setQueueSummary(q);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runDiag = async () => {
    setDiagLoading(true);
    try {
      const { data } = await api.get("/admin/r2/diagnostics");
      setDiag(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Diagnostics failed");
    } finally {
      setDiagLoading(false);
    }
  };

  const [tutorial, setTutorial] = useState(null);
  const [tutorialInput, setTutorialInput] = useState("");
  const loadTutorial = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/tutorial");
      setTutorial(data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadTutorial(); }, [loadTutorial]);

  const pinTutorial = async () => {
    if (!tutorialInput.trim()) return;
    try {
      await api.post(`/admin/tutorial/${tutorialInput.trim()}`);
      toast.success("Tutorial pinned for new users");
      setTutorialInput("");
      await loadTutorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const unpinTutorial = async () => {
    try {
      await api.delete("/admin/tutorial");
      toast.success("Tutorial unpinned");
      await loadTutorial();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const purgeFailed = async () => {
    if (!window.confirm("Delete all failed uploads and their draft posts? This is irreversible.")) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/maintenance/purge-failed-uploads");
      toast.success(`Purged ${data.total_sessions_deleted} sessions + ${data.pending_posts_deleted} drafts`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Purge failed");
    } finally {
      setPurging(false);
    }
  };

  const purgeQuantPulse = async () => {
    if (!window.confirm("Permanently erase all old QuantPulse trading data? This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/maintenance/purge-quantpulse");
      toast.success(`Erased ${data.collections_dropped.length} trading tables + ${data.notifications_deleted} alerts`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erase failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Upload queue</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {queueSummary ? (
            <>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-3">
                {["init", "uploading", "queued", "processing", "ready", "failed", "cancelled"].map((k) => (
                  <div key={k} className="rounded-lg bg-white/5 border border-white/10 p-3 text-center" data-testid={`queue-${k}`}>
                    <div className="text-lg font-heading font-black">{queueSummary.counts?.[k] || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-white/50">{k}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button
                  data-testid="purge-failed-uploads-btn"
                  onClick={purgeFailed}
                  disabled={purging}
                  className="bg-red-600 hover:bg-red-700 gap-2"
                >
                  <Trash size={14} weight="bold" />
                  {purging ? "Purging…" : "Purge failed uploads"}
                </Button>
                <Button
                  data-testid="purge-quantpulse-btn"
                  onClick={purgeQuantPulse}
                  className="bg-red-600 hover:bg-red-700 gap-2"
                >
                  <Trash size={14} weight="bold" />
                  Erase old QuantPulse data
                </Button>
                <Button
                  onClick={load}
                  variant="outline"
                  className="border-white/20 bg-transparent hover:bg-white/5"
                >
                  Refresh
                </Button>
              </div>
              <p className="text-xs text-white/50">
                Purge deletes failed / cancelled upload sessions, their staged chunks, and any pending-payment posts linked
                to them. Also cleans orphaned sessions where chunks are gone (container recycled). Running uploads are untouched.
              </p>
            </>
          ) : (
            <p className="text-white/50 text-xs">Loading…</p>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">New-user tutorial video</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-white/70">
            Pin one live video as the platform tutorial. Every new user sees it at the top of their For-You feed
            for their first 10 views (or 30 days, whichever comes first). Paste the <code>post_id</code> of a live
            admin-uploaded video (visible in the post URL after the last <code>/</code>).
          </p>
          <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-sm" data-testid="tutorial-current">
            {tutorial?.post_id ? (
              <>
                <div className="text-emerald-400 font-heading font-bold">Currently pinned:</div>
                <div className="text-white/80 text-xs mt-1 break-all">{tutorial.post_id}</div>
                {tutorial.caption && <div className="text-white/50 text-xs mt-1 line-clamp-2">{tutorial.caption}</div>}
                <Button
                  data-testid="tutorial-unpin-btn"
                  onClick={unpinTutorial}
                  variant="outline"
                  size="sm"
                  className="mt-2 border-red-500/30 bg-transparent text-red-300 hover:bg-red-500/10"
                >
                  Unpin
                </Button>
              </>
            ) : (
              <div className="text-white/50">No tutorial pinned. Placeholder shown to new users until one is set.</div>
            )}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="tutorial-input"
              value={tutorialInput}
              onChange={(e) => setTutorialInput(e.target.value)}
              placeholder="post_xxxxxxxxxxxxxx"
              className="flex-1 bg-white/5 border border-white/10 rounded px-3 py-2 text-sm font-mono"
            />
            <Button
              data-testid="tutorial-pin-btn"
              onClick={pinTutorial}
              disabled={!tutorialInput.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Pin as tutorial
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Cloudflare R2 diagnostics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-white/70">
            Verifies R2 credentials work and that CORS is enabled — hls.js needs CORS to fetch adaptive video segments from the browser.
          </p>
          <Button
            data-testid="r2-diag-btn"
            onClick={runDiag}
            disabled={diagLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {diagLoading ? "Testing…" : "Run diagnostics"}
          </Button>
          {diag && (
            <div className="rounded-lg border border-white/10 bg-white/5 p-4 text-sm space-y-1" data-testid="r2-diag-result">
              <div className={`font-heading font-black ${diag.configured ? "text-emerald-400" : "text-red-400"}`}>
                {diag.configured ? "R2 configured" : "R2 not configured"}
              </div>
              <div>Upload: {diag.upload_ok ? "OK" : "FAILED"}</div>
              <div>Public GET: {diag.public_ok ? "OK" : "FAILED"}</div>
              <div className={diag.cors_ok ? "text-emerald-400" : "text-yellow-300"}>
                CORS: {diag.cors_ok ? "OK — hls.js can load videos" : "NOT SET (see hint below)"}
              </div>
              {diag.hint && (
                <p className="text-yellow-200/80 text-xs mt-2 leading-relaxed">{diag.hint}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PaymentsPanel() {
  const [posts, setPosts] = useState([]);
  const [previewing, setPreviewing] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/pending-payments");
    setPosts(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    try {
      await api.post(`/admin/posts/${id}/approve`);
      toast.success("Post approved & now live");
      setPreviewing(null);
      load();
    } catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    try {
      await api.post(`/admin/posts/${id}/reject`);
      toast.success("Post rejected");
      setPreviewing(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${backend}/api/files/${u}`);

  return (
    <>
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading">Pending payments — review & approve</CardTitle>
        </CardHeader>
        <CardContent>
          {posts.length === 0 ? (
            <p className="text-white/50 text-sm py-4">Nothing awaiting approval.</p>
          ) : (
            <div className="space-y-3">
              {posts.map((p) => (
                <div
                  key={p.post_id}
                  className="p-4 rounded-lg bg-white/5 border border-white/10 flex items-center gap-3"
                  data-testid={`pending-row-${p.post_id}`}
                >
                  {(p.thumbnail_url || p.media_url) && p.content_type !== "text" ? (
                    <img
                      src={resolve(p.thumbnail_url || p.media_url)}
                      alt=""
                      className="w-16 h-20 object-cover rounded bg-black flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-20 rounded bg-white/5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">{p.content_type}</Badge>
                      <p className="font-semibold text-sm">@{p.author?.name || "unknown"}</p>
                      <p className="text-xs text-white/40">${(p.publish_fee || 0).toFixed(2)}</p>
                    </div>
                    <p className="text-sm text-white/70 truncate mt-1">{p.caption || "(no caption)"}</p>
                    <div className="text-[11px] text-white/50 mt-1 space-x-3">
                      {p.author?.email && <span>{p.author.email}</span>}
                      {p.author?.phone && <span>· {p.author.phone}</span>}
                      {p.author?.whatsapp && <span>· wa: {p.author.whatsapp}</span>}
                    </div>
                  </div>
                  <Button
                    data-testid={`preview-post-${p.post_id}`}
                    onClick={() => setPreviewing(p)}
                    size="sm"
                    variant="outline"
                    className="border-white/20 bg-transparent hover:bg-white/5 gap-1"
                  >
                    <Eye size={16} weight="bold" /> Review
                  </Button>
                  <Button
                    data-testid={`approve-post-${p.post_id}`}
                    onClick={() => approve(p.post_id)}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                  >
                    <CheckCircle size={16} weight="bold" /> Approve
                  </Button>
                  <Button
                    data-testid={`reject-post-${p.post_id}`}
                    onClick={() => reject(p.post_id)}
                    variant="outline"
                    size="sm"
                    className="border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent gap-1"
                  >
                    <XCircle size={16} weight="bold" /> Reject
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {previewing && (
        <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
          <DialogContent className="max-w-3xl bg-[#0d0d0d] border-white/10 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading">Review submission</DialogTitle>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-black rounded-lg overflow-hidden aspect-[9/16]">
                {previewing.content_type === "video" && previewing.media_url && (
                  <video src={resolve(previewing.media_url)} controls className="w-full h-full object-contain" />
                )}
                {previewing.content_type === "image" && previewing.media_url && (
                  <img src={resolve(previewing.media_url)} alt="" className="w-full h-full object-contain" />
                )}
                {previewing.content_type === "text" && (
                  <div className="p-6 h-full flex items-center justify-center">
                    <p className="text-white/90 whitespace-pre-wrap">{previewing.caption}</p>
                  </div>
                )}
              </div>
              <div className="space-y-3 text-sm" data-testid="preview-detail">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/40">Creator</div>
                  <div className="font-heading font-bold">{previewing.author?.name || "unknown"}</div>
                  <div className="text-white/60 text-xs mt-1">
                    {previewing.author?.email && <div>📧 {previewing.author.email}</div>}
                    {previewing.author?.phone && <div>📞 {previewing.author.phone}</div>}
                    {previewing.author?.whatsapp && (
                      <div>
                        💬 WhatsApp:{" "}
                        <a
                          href={`https://wa.me/${previewing.author.whatsapp.replace(/[^0-9]/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-emerald-400 underline"
                        >
                          {previewing.author.whatsapp}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                {previewing.author?.bio && (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-white/40">Bio</div>
                    <p className="text-white/70 text-xs">{previewing.author.bio}</p>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-white/40">Caption</div>
                  <p className="text-white/85 text-xs whitespace-pre-wrap">{previewing.caption || "(none)"}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-white/40">Type:</span> {previewing.content_type}</div>
                  <div><span className="text-white/40">Category:</span> {previewing.category || "—"}</div>
                  <div><span className="text-white/40">Fee:</span> ${(previewing.publish_fee || 0).toFixed(2)}</div>
                  <div><span className="text-white/40">Privacy:</span> {previewing.privacy || "public"}</div>
                </div>
                {previewing.payment && (
                  <div className="rounded border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs">
                    <div className="text-emerald-300 font-heading font-bold text-[10px] uppercase tracking-widest">Payment</div>
                    <div className="text-white/70 mt-1">
                      {previewing.payment.method || "points"} · {(previewing.payment.amount || previewing.payment.points || 0)}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <Button
                    data-testid={`preview-approve-${previewing.post_id}`}
                    onClick={() => approve(previewing.post_id)}
                    className="bg-emerald-600 hover:bg-emerald-700 flex-1 gap-1"
                  >
                    <CheckCircle size={16} weight="bold" /> Approve
                  </Button>
                  <Button
                    data-testid={`preview-reject-${previewing.post_id}`}
                    onClick={() => reject(previewing.post_id)}
                    variant="outline"
                    className="border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent flex-1 gap-1"
                  >
                    <XCircle size={16} weight="bold" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this user and all their content?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleBan = async (u) => {
    const action = u.banned ? "unban" : "ban";
    if (!window.confirm(`${action.toUpperCase()} this user?`)) return;
    try {
      await api.post(`/admin/users/${u.user_id}/${action}`);
      toast.success(`User ${action}ned`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const markTest = async (id) => {
    await api.post(`/admin/users/${id}/mark-test`);
    toast.success("Marked as test");
    load();
  };

  const cleanupTests = async () => {
    if (!window.confirm("Delete all users marked as test?")) return;
    const { data } = await api.post("/admin/cleanup-test-users");
    toast.success(`Deleted ${data.deleted} test users`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          data-testid="cleanup-tests-btn"
          onClick={cleanupTests}
          variant="outline"
          className="border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent"
        >
          Cleanup all test accounts
        </Button>
      </div>
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-0">
          <div className="divide-y divide-white/5">
            {users.map((u) => (
              <div key={u.user_id} className="flex items-center gap-3 p-4">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-9 h-9 rounded-full" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm">{u.name}</p>
                    {u.role === "admin" && <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">Admin</Badge>}
                    {u.is_test && <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/40">Test</Badge>}
                    {u.banned && <Badge className="bg-red-500/20 text-red-300 border-red-500/40">Banned</Badge>}
                  </div>
                  <p className="text-xs text-white/40 truncate">{u.email}</p>
                  {u.phone_number ? (
                    <a
                      data-testid={`user-phone-${u.user_id}`}
                      href={`https://wa.me/${u.phone_number.replace(/[^0-9]/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#25D366] hover:underline flex items-center gap-1 mt-0.5"
                    >
                      <WhatsappLogo size={12} weight="fill" />
                      {u.phone_number}
                    </a>
                  ) : (
                    <p className="text-xs text-white/30 flex items-center gap-1 mt-0.5">
                      <Phone size={12} weight="bold" />
                      No phone
                    </p>
                  )}
                </div>
                <p className="font-heading font-black text-emerald-400">${u.balance.toFixed(2)}</p>
                <Button
                  data-testid={`mark-test-${u.user_id}`}
                  onClick={() => markTest(u.user_id)}
                  size="sm"
                  variant="outline"
                  className="border-white/20 bg-transparent hover:bg-white/5 text-xs"
                >
                  Mark test
                </Button>
                {u.role !== "admin" && (
                  <>
                    <Button
                      data-testid={`ban-user-${u.user_id}`}
                      onClick={() => toggleBan(u)}
                      size="sm"
                      variant="outline"
                      className={
                        u.banned
                          ? "border-emerald-500/40 bg-transparent hover:bg-emerald-500/10 text-emerald-400"
                          : "border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400"
                      }
                    >
                      <Prohibit size={14} weight="bold" />
                    </Button>
                    <Button
                      data-testid={`delete-user-${u.user_id}`}
                      onClick={() => del(u.user_id)}
                      size="sm"
                      variant="outline"
                      className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400"
                    >
                      <Trash size={14} weight="bold" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function AdsPanel() {
  const [ads, setAds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [clicksFor, setClicksFor] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", media_url: "", whatsapp_link: "", target_views: 100, click_cost: 0.2,
  });

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/ads");
    setAds(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/admin/ads", {
        ...form,
        target_views: parseInt(form.target_views),
        click_cost: parseFloat(form.click_cost),
      });
      toast.success("Ad created");
      setShowCreate(false);
      setForm({ title: "", description: "", media_url: "", whatsapp_link: "", target_views: 100, click_cost: 0.2 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete ad?")) return;
    await api.delete(`/admin/ads/${id}`);
    load();
  };

  const toggle = async (ad) => {
    await api.put(`/admin/ads/${ad.ad_id}`, { is_active: !ad.is_active });
    load();
  };

  const viewClicks = async (ad) => {
    setClicksFor(ad);
    const { data } = await api.get(`/admin/ads/${ad.ad_id}/clicks`);
    setClicks(data);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          data-testid="create-ad-btn"
          onClick={() => setShowCreate(true)}
          className="bg-blue-600 hover:bg-blue-700 gap-2"
        >
          <MegaphoneSimple size={16} weight="bold" /> New Ad
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {ads.map((ad) => (
          <Card key={ad.ad_id} className="bg-[#121212] border-white/10 overflow-hidden">
            <img src={/^https?:/.test(ad.media_url) ? ad.media_url : `${process.env.REACT_APP_BACKEND_URL}/api/files/${ad.media_url}`} alt="" className="w-full h-40 object-cover" />
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold truncate">{ad.title}</p>
                  <p className="text-xs text-white/50 line-clamp-2">{ad.description}</p>
                </div>
                <Badge className={ad.is_active ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/10 text-white/50 border-white/20"}>
                  {ad.is_active ? "Active" : "Paused"}
                </Badge>
              </div>
              <div className="grid grid-cols-3 text-center text-xs">
                <div>
                  <p className="text-white/50">Views</p>
                  <p className="font-bold">{ad.current_views}/{ad.target_views}</p>
                </div>
                <div>
                  <p className="text-white/50">Clicks</p>
                  <p className="font-bold text-[#25D366]">{ad.click_count}</p>
                </div>
                <div>
                  <p className="text-white/50">Cost/click</p>
                  <p className="font-bold">${ad.click_cost.toFixed(2)}</p>
                </div>
              </div>
              <p className="text-xs text-emerald-400">
                Est. click revenue: ${(ad.click_count * ad.click_cost).toFixed(2)}
              </p>
              <div className="flex gap-2">
                <Button
                  data-testid={`view-clicks-${ad.ad_id}`}
                  onClick={() => viewClicks(ad)}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/20 bg-transparent hover:bg-white/5"
                >
                  <Eye size={14} weight="bold" className="mr-1" /> Clicks
                </Button>
                <Button
                  data-testid={`toggle-ad-${ad.ad_id}`}
                  onClick={() => toggle(ad)}
                  size="sm"
                  variant="outline"
                  className="flex-1 border-white/20 bg-transparent hover:bg-white/5"
                >
                  {ad.is_active ? "Pause" : "Activate"}
                </Button>
                <Button
                  data-testid={`delete-ad-${ad.ad_id}`}
                  onClick={() => remove(ad.ad_id)}
                  size="sm"
                  variant="outline"
                  className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-400"
                >
                  <Trash size={14} weight="bold" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {ads.length === 0 && <p className="text-white/50 col-span-2">No ads yet.</p>}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">New advertisement</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input data-testid="ad-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border-white/10" />
            <Textarea data-testid="ad-description" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10" />
            <FileUpload
              accept="image/*"
              label="Ad image"
              value={form.media_url}
              onChange={(url) => setForm({ ...form, media_url: url })}
              testId="ad-media-upload"
            />
            <Input data-testid="ad-whatsapp" placeholder="https://wa.me/1234567890" value={form.whatsapp_link} onChange={(e) => setForm({ ...form, whatsapp_link: e.target.value })} className="bg-white/5 border-white/10" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-white/60">Target views</label>
                <Input data-testid="ad-target-views" type="number" value={form.target_views} onChange={(e) => setForm({ ...form, target_views: e.target.value })} className="bg-white/5 border-white/10" />
              </div>
              <div>
                <label className="text-xs text-white/60">Click cost ($)</label>
                <Input data-testid="ad-click-cost" type="number" step="0.01" value={form.click_cost} onChange={(e) => setForm({ ...form, click_cost: e.target.value })} className="bg-white/5 border-white/10" />
              </div>
            </div>
            <Button data-testid="ad-save-btn" onClick={create} className="w-full bg-blue-600 hover:bg-blue-700">
              Create ad
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clicks dialog */}
      <Dialog open={!!clicksFor} onOpenChange={(v) => !v && setClicksFor(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Clicks: {clicksFor?.title}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {clicks.length === 0 ? (
              <p className="text-white/50 text-sm">No clicks yet.</p>
            ) : (
              clicks.map((c) => (
                <div key={c.ad_click_id} className="flex justify-between p-2 rounded bg-white/5">
                  <span className="text-sm">{c.user?.name || "Anon"}</span>
                  <span className="text-xs text-white/50">{new Date(c.created_at).toLocaleString()}</span>
                </div>
              ))
            )}
          </div>
          <div className="pt-3 border-t border-white/10 flex justify-between">
            <span className="text-sm text-white/60">Total clicks</span>
            <span className="font-heading font-bold text-[#25D366]">{clicks.length}</span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WithdrawalsPanel() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/withdrawals");
    setWithdrawals(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const note = window.prompt("Optional payment reference / note:") || "";
    try {
      await api.post(`/admin/withdrawals/${id}/approve`, { note });
      toast.success("Marked as paid");
      load();
    } catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    const note = window.prompt("Reason for rejection:") || "";
    if (!note.trim()) return toast.error("Reason required");
    try {
      await api.post(`/admin/withdrawals/${id}/reject`, { note });
      toast.success("Rejected & refunded");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = withdrawals.filter((w) => filter === "all" || w.status === filter);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {["pending", "paid", "rejected", "all"].map((f) => (
          <Button
            key={f}
            data-testid={`filter-wd-${f}`}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
            className={
              filter === f
                ? "bg-blue-600 hover:bg-blue-700"
                : "border-white/20 bg-transparent hover:bg-white/5"
            }
          >
            {f}
          </Button>
        ))}
      </div>
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="text-white/50 text-sm text-center py-8">No withdrawals.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map((w) => (
                <div key={w.withdrawal_id} className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-heading font-black text-xl">${w.amount.toFixed(2)}</p>
                      <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/40">
                        {w.method.toUpperCase()}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={
                          w.status === "paid"
                            ? "border-emerald-500/40 text-emerald-400"
                            : w.status === "rejected"
                            ? "border-red-500/40 text-red-400"
                            : "border-yellow-500/40 text-yellow-400"
                        }
                      >
                        {w.status}
                      </Badge>
                    </div>
                    <p className="text-sm mt-1">
                      <span className="text-white/50">{w.user?.name}</span> ({w.user?.email})
                    </p>
                    <p className="text-xs text-white/60 mt-0.5">
                      {w.account_name} • {w.account_details}
                      {w.bank_name && ` • ${w.bank_name}`}
                    </p>
                    {w.admin_note && (
                      <p className="text-xs text-white/50 italic mt-1">note: {w.admin_note}</p>
                    )}
                  </div>
                  {w.status === "pending" && (
                    <div className="flex gap-2">
                      <Button
                        data-testid={`wd-approve-${w.withdrawal_id}`}
                        onClick={() => approve(w.withdrawal_id)}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                      >
                        <CheckCircle size={14} weight="bold" /> Mark paid
                      </Button>
                      <Button
                        data-testid={`wd-reject-${w.withdrawal_id}`}
                        onClick={() => reject(w.withdrawal_id)}
                        size="sm"
                        variant="outline"
                        className="border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent gap-1"
                      >
                        <XCircle size={14} weight="bold" /> Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/reports");
    setReports(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Remove this ${r.target_type}?`)) return;
    await api.post(`/admin/reports/${r.target_type}/${r.target_id}/remove`);
    toast.success("Removed");
    load();
  };
  const dismiss = async (r) => {
    await api.post(`/admin/reports/${r.target_type}/${r.target_id}/dismiss`);
    toast.success("Dismissed");
    load();
  };

  return (
    <div className="space-y-4">
      {reports.length === 0 ? (
        <p className="text-white/50 text-sm py-8 text-center">No pending reports.</p>
      ) : (
        reports.map((r) => (
          <Card key={`${r.target_type}-${r.target_id}`} className="bg-[#121212] border-white/10">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Badge className="bg-red-500/20 text-red-300 border-red-500/40">
                  {r.target_type}
                </Badge>
                <span className="text-sm text-white/70">
                  {r.reports.length} report{r.reports.length === 1 ? "" : "s"}
                </span>
              </div>
              {r.target ? (
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  {r.target_type === "post" ? (
                    <>
                      <p className="text-xs text-white/50">by @{r.target.author?.name}</p>
                      {r.target.media_url && (
                        <img src={r.target.media_url} alt="" className="w-full h-32 object-cover rounded mt-2" />
                      )}
                      <p className="text-sm mt-2 line-clamp-3">{r.target.caption || "(no caption)"}</p>
                    </>
                  ) : (
                    <p className="text-sm">{r.target.text}</p>
                  )}
                </div>
              ) : (
                <p className="text-white/50 text-sm italic">Target deleted</p>
              )}
              <div className="space-y-1">
                {r.reports.map((rep) => (
                  <div key={rep.report_id} className="text-xs text-white/70">
                    • {rep.reason}
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button
                  data-testid={`report-remove-${r.target_id}`}
                  onClick={() => remove(r)}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 gap-1"
                >
                  <Trash size={14} weight="bold" /> Remove content
                </Button>
                <Button
                  data-testid={`report-dismiss-${r.target_id}`}
                  onClick={() => dismiss(r)}
                  size="sm"
                  variant="outline"
                  className="border-white/20 bg-transparent hover:bg-white/5 gap-1"
                >
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function AppealsPanel() {
  const [appeals, setAppeals] = useState([]);
  const load = useCallback(async () => {
    const { data } = await api.get("/admin/appeals");
    setAppeals(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    const note = window.prompt(`Admin note for ${action} (optional):`) || "";
    try {
      await api.post(`/admin/appeals/${id}/${action}`, { note });
      toast.success(action === "approve" ? "Appeal approved & user unbanned" : "Appeal rejected");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <Card className="bg-[#121212] border-white/10">
      <CardContent className="p-0">
        {appeals.length === 0 ? (
          <p className="text-white/50 text-sm text-center py-8">No pending appeals.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {appeals.map((a) => (
              <div key={a.appeal_id} className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  {a.user?.picture ? (
                    <img src={a.user.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/10" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold">{a.user?.name}</p>
                    <p className="text-xs text-white/50 truncate">{a.user?.email}</p>
                  </div>
                  <span className="text-xs text-white/50">
                    {new Date(a.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm bg-white/5 p-3 rounded-lg border border-white/10">
                  {a.message}
                </p>
                <div className="flex gap-2">
                  <Button
                    data-testid={`appeal-approve-${a.appeal_id}`}
                    onClick={() => decide(a.appeal_id, "approve")}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                  >
                    <CheckCircle size={14} weight="bold" /> Approve & unban
                  </Button>
                  <Button
                    data-testid={`appeal-reject-${a.appeal_id}`}
                    onClick={() => decide(a.appeal_id, "reject")}
                    size="sm"
                    variant="outline"
                    className="border-red-500/40 text-red-400 hover:bg-red-500/10 bg-transparent gap-1"
                  >
                    <XCircle size={14} weight="bold" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WeeklyPrizePanel() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [paying, setPaying] = useState(false);
  const [poolInput, setPoolInput] = useState("");
  const [poolMode, setPoolMode] = useState("set");
  const [poolSaving, setPoolSaving] = useState(false);

  const load = useCallback(async () => {
    const [c, h] = await Promise.all([
      api.get("/weekly-prize/current"),
      api.get("/weekly-prize/history"),
    ]);
    setCurrent(c.data);
    setHistory(h.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePool = async () => {
    const amount = parseFloat(poolInput);
    if (isNaN(amount) || amount < 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setPoolSaving(true);
    try {
      const { data } = await api.put("/admin/weekly-prize/pool", { amount, mode: poolMode });
      toast.success(`Pool now $${data.pool.toFixed(2)}`);
      setPoolInput("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setPoolSaving(false);
    }
  };

  const payout = async () => {
    if (!window.confirm("Distribute the oldest unpaid weekly pool to top 3 (50/30/20)?")) return;
    setPaying(true);
    try {
      const { data } = await api.post("/admin/weekly-prize/payout");
      toast.success(`Paid out week ${data.week} to ${data.winners.length} winner(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setPaying(false);
    }
  };

  if (!current) return <div className="text-white/60">Loading…</div>;

  return (
    <div className="space-y-4">
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Current week — {current.week_start}</CardTitle>
          <Button
            data-testid="weekly-payout-btn"
            onClick={payout}
            disabled={paying || current.pool <= 0}
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold gap-2"
          >
            <Crown size={16} weight="fill" /> Payout oldest unpaid
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="font-heading text-4xl font-black text-yellow-400">
            ${current.pool.toFixed(2)}
          </p>
          <p className="text-xs text-white/50">
            Auto-funded from {current.prize_pct}% of publish fees + any manual top-ups. Distribution: 50% / 30% / 20%.
          </p>
          <div className="pt-3 border-t border-white/10 space-y-2">
            <p className="text-xs uppercase tracking-widest font-bold text-white/50">Manual pool amount</p>
            <div className="flex flex-wrap gap-2">
              <select
                data-testid="pool-mode-select"
                value={poolMode}
                onChange={(e) => setPoolMode(e.target.value)}
                className="bg-white/5 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
              >
                <option value="set">Set to</option>
                <option value="add">Add</option>
              </select>
              <Input
                data-testid="pool-amount-input"
                type="number"
                min="0"
                step="0.01"
                value={poolInput}
                onChange={(e) => setPoolInput(e.target.value)}
                placeholder="Amount in $"
                className="bg-white/5 border-white/10 text-white flex-1 min-w-[140px]"
              />
              <Button
                data-testid="pool-save-btn"
                onClick={savePool}
                disabled={poolSaving || poolInput === ""}
                className="bg-blue-600 hover:bg-blue-700 font-bold"
              >
                {poolSaving ? "Saving…" : "Apply"}
              </Button>
            </div>
            <p className="text-[11px] text-white/40">
              &quot;Set to&quot; replaces the pool; &quot;Add&quot; tops it up on the existing amount.
            </p>
          </div>
          {current.top.length > 0 && (
            <div className="space-y-2 pt-2">
              <p className="text-xs uppercase tracking-widest font-bold text-white/50">Live ranking</p>
              {current.top.slice(0, 10).map((u, i) => (
                <div key={u.user_id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-b-0">
                  <span className="w-6 font-heading font-bold text-white/50">{i + 1}</span>
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-white/10" />
                  )}
                  <p className="flex-1 font-semibold text-sm truncate">{u.name}</p>
                  <p className="text-sm text-emerald-400 font-bold">${u.weekly_earnings.toFixed(2)}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Past payouts</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <p className="text-white/50 text-sm">No payouts yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map((h) => (
                <div key={h.week_key} className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-sm font-heading font-bold">
                    Week {h.week_key} • ${h.pool.toFixed(2)}
                  </p>
                  <div className="flex gap-3 text-xs mt-1">
                    {h.winners.map((w) => (
                      <span key={w.rank} className="text-white/70">
                        #{w.rank} {w.name} — <span className="text-emerald-400">${w.amount.toFixed(2)}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AnnouncementsPanel() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!title.trim()) return toast.error("Title is required");
    if (!window.confirm(`Broadcast "${title}" to all non-banned users?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/announcements", {
        title: title.trim(),
        body: body.trim(),
      });
      toast.success(`Delivered to ${data.delivered_to} users`);
      setTitle("");
      setBody("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="bg-[#121212] border-white/10 max-w-2xl">
      <CardHeader>
        <CardTitle className="font-heading">Broadcast announcement</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-white/60">
          Sends an in-app notification (with sound) to every non-banned user.
        </p>
        <div>
          <label className="text-sm text-white/70">Title</label>
          <Input
            data-testid="announcement-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. New feature launched!"
            className="bg-white/5 border-white/10 text-white mt-1"
          />
        </div>
        <div>
          <label className="text-sm text-white/70">Message (optional)</label>
          <Textarea
            data-testid="announcement-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add details users should know…"
            rows={4}
            className="bg-white/5 border-white/10 text-white mt-1"
          />
        </div>
        <Button
          data-testid="announcement-send-btn"
          onClick={send}
          disabled={sending}
          className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
        >
          <PaperPlaneTilt size={18} weight="fill" />
          Send to all users
        </Button>
      </CardContent>
    </Card>
  );
}


import SchoolsAdmin from "@/pages/SchoolsAdmin";
import LegalGameAdmin from "@/pages/LegalGameAdmin";


export default function Admin() {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/feed" replace />;

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl font-black">Admin</h1>
        <p className="text-white/60 mt-1">Manage the platform, review payments and run ads.</p>
      </div>

      <Tabs defaultValue="stats">
        <TabsList className="bg-white/5 flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-stats" value="stats" className="gap-2"><ChartLine size={16} /> Stats</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-payments" value="payments" className="gap-2"><CurrencyDollar size={16} /> Payments</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-point-sales" value="point-sales" className="gap-2"><CurrencyDollar size={16} /> Point Sales</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-withdrawals" value="withdrawals" className="gap-2"><Bank size={16} /> Withdrawals</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-reports" value="reports" className="gap-2"><Flag size={16} /> Reports</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-appeals" value="appeals" className="gap-2"><Envelope size={16} /> Appeals</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-weekly" value="weekly" className="gap-2"><Crown size={16} /> Weekly Prize</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-announcements" value="announcements" className="gap-2"><PaperPlaneTilt size={16} /> Announce</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-ads" value="ads" className="gap-2"><MegaphoneSimple size={16} /> Ads</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-users" value="users" className="gap-2"><Users size={16} /> Users</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-jobs" value="jobs" className="gap-2"><Briefcase size={16} /> Jobs</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-schools" value="schools" className="gap-2"><GraduationCap size={16} /> Schools</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-points-economy" value="points-economy" className="gap-2"><Coins size={16} /> Credits economy</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-revenue-share" value="revenue-share" className="gap-2"><ChartLine size={16} /> Revenue Share</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-games" value="games" className="gap-2"><Coins size={16} /> Games</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-legal" value="legal" className="gap-2"><Gavel size={16} /> Legal Game</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-market" value="market" className="gap-2"><CurrencyDollar size={16} /> Tainapp Market</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-maintenance" value="maintenance" className="gap-2"><VideoCamera size={16} /> Maintenance</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-settings" value="settings" className="gap-2"><Gear size={16} /> Settings</TabsTrigger>
        </TabsList>
        <TabsContent value="stats" className="mt-6"><StatsPanel /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsPanel /></TabsContent>
        <TabsContent value="point-sales" className="mt-6"><PointPurchasesPanel /></TabsContent>
        <TabsContent value="withdrawals" className="mt-6"><WithdrawalsPanel /></TabsContent>
        <TabsContent value="reports" className="mt-6"><ReportsPanel /></TabsContent>
        <TabsContent value="appeals" className="mt-6"><AppealsPanel /></TabsContent>
        <TabsContent value="weekly" className="mt-6"><WeeklyPrizePanel /></TabsContent>
        <TabsContent value="announcements" className="mt-6"><AnnouncementsPanel /></TabsContent>
        <TabsContent value="ads" className="mt-6"><AdsPanel /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersPanel /></TabsContent>
        <TabsContent value="jobs" className="mt-6"><JobsAdminPanel /></TabsContent>
        <TabsContent value="schools" className="mt-6"><SchoolsAdmin /></TabsContent>
        <TabsContent value="points-economy" className="mt-6"><PointsAdminPanel /></TabsContent>
        <TabsContent value="revenue-share" className="mt-6"><RevenueShareAdminPanel /></TabsContent>
        <TabsContent value="games" className="mt-6"><GamesAdminPanel /></TabsContent>
        <TabsContent value="legal" className="mt-6"><LegalGameAdmin /></TabsContent>
        <TabsContent value="market" className="mt-6"><MarketAdminPanel /></TabsContent>
        <TabsContent value="maintenance" className="mt-6"><VideoMaintenancePanel /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function PointPurchasesPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/point-purchases", { params: filter ? { status: filter } : {} });
      setItems(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (id) => {
    if (!window.confirm("Confirm payment received? Credits will be credited immediately.")) return;
    try {
      await api.post(`/admin/point-purchases/${id}/mark-paid`);
      toast.success("Marked as paid — points credited");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this purchase request?")) return;
    try {
      await api.post(`/admin/point-purchases/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {["pending", "paid", "rejected", ""].map((f) => (
          <button
            key={f || "all"}
            data-testid={`points-filter-${f || "all"}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-sm font-heading font-bold ${
              filter === f ? "bg-blue-600 text-white" : "bg-white/5 text-white/70 hover:bg-white/10"
            }`}
          >
            {f || "All"}
          </button>
        ))}
      </div>
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-0">
          {loading ? (
            <p className="p-6 text-white/60 text-sm">Loading…</p>
          ) : items.length === 0 ? (
            <p className="p-6 text-white/50 text-sm text-center">No purchases in this state.</p>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((p) => (
                <div key={p.purchase_id} className="p-4 flex items-center gap-3">
                  {p.user?.picture ? (
                    <img src={p.user.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/10" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm truncate">{p.user?.name || p.user_id}</p>
                    <p className="text-xs text-white/50">{p.user?.email}</p>
                    <p className="text-xs text-white/50 mt-0.5">
                      {new Date(p.created_at).toLocaleString()} • {p.status}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading text-lg font-black text-yellow-300">{p.points} cr</p>
                    <p className="text-xs text-white/50">${p.amount_usd.toFixed(2)}</p>
                  </div>
                  {p.status === "pending" && (
                    <div className="flex gap-1 shrink-0">
                      <Button
                        data-testid={`points-mark-paid-${p.purchase_id}`}
                        size="sm"
                        onClick={() => markPaid(p.purchase_id)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        Mark paid
                      </Button>
                      <Button
                        data-testid={`points-reject-${p.purchase_id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => reject(p.purchase_id)}
                        className="border-white/20 bg-transparent hover:bg-white/5"
                      >
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ============ JOBS & CAREERS ADMIN ============
function JobsAdminPanel() {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [pendingV, setPendingV] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, j, v] = await Promise.all([
        api.get("/admin/jobs/stats").then((r) => r.data),
        api.get("/admin/jobs").then((r) => r.data),
        api.get("/admin/employer-verifications").then((r) => r.data),
      ]);
      setStats(s); setJobs(j.items || []); setPendingV(v.items || []);
    } catch { toast.error("Failed to load jobs admin"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (jobId, act) => {
    try {
      await api.post(`/admin/jobs/${jobId}/action`, { action: act });
      toast.success("Done");
      load();
    } catch { toast.error("Action failed"); }
  };

  const verify = async (userId, approved) => {
    try {
      await api.post(`/admin/employer/${userId}/verify`, { approved });
      toast.success(approved ? "Employer verified" : "Rejected");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatBox label="Total jobs" value={stats.jobs_total} />
          <StatBox label="Open" value={stats.jobs_open} />
          <StatBox label="Seekers" value={stats.seekers_total} />
          <StatBox label="Applications" value={stats.applications_total} />
        </div>
      )}

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Pending employer verifications</CardTitle></CardHeader>
        <CardContent>
          {pendingV.length === 0 ? <p className="text-white/40 text-sm">Nothing pending.</p> : (
            <div className="space-y-2">
              {pendingV.map((v) => (
                <div key={v.user_id} className="flex items-center gap-3 p-3 rounded-lg border border-white/10 bg-white/5">
                  <div className="flex-1">
                    <div className="font-heading font-bold">{v.user?.name || v.user_id}</div>
                    <div className="text-xs text-white/50">{v.user?.email}</div>
                  </div>
                  <Button data-testid={`verify-approve-${v.user_id}`} onClick={() => verify(v.user_id, true)} className="bg-emerald-600 hover:bg-emerald-700" size="sm">Approve</Button>
                  <Button data-testid={`verify-reject-${v.user_id}`} onClick={() => verify(v.user_id, false)} variant="outline" size="sm" className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-300">Reject</Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">All job listings</CardTitle></CardHeader>
        <CardContent>
          {loading && <p className="text-white/50 text-sm">Loading…</p>}
          {!loading && jobs.length === 0 && <p className="text-white/40 text-sm">No jobs yet.</p>}
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
            {jobs.map((j) => (
              <div key={j.job_id} className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold">{j.title}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-black ${j.status === "removed" ? "bg-red-500/15 text-red-300" : j.status === "open" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/60"}`}>{j.status}</span>
                    {j.featured && <span className="px-1.5 py-0.5 rounded text-[10px] uppercase font-black bg-yellow-500/15 text-yellow-300">Featured</span>}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">{j.employer_company_name} · {j.location} · {j.application_count || 0} apps · {j.view_count || 0} views</div>
                </div>
                <div className="flex gap-1.5">
                  {j.status === "removed" ? (
                    <Button data-testid={`admin-restore-${j.job_id}`} onClick={() => action(j.job_id, "restore")} size="sm" variant="outline" className="border-white/20 bg-transparent hover:bg-white/5">Restore</Button>
                  ) : (
                    <Button data-testid={`admin-remove-${j.job_id}`} onClick={() => action(j.job_id, "remove")} size="sm" variant="outline" className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-300">Remove</Button>
                  )}
                  {j.featured ? (
                    <Button data-testid={`admin-unfeature-${j.job_id}`} onClick={() => action(j.job_id, "unfeature")} size="sm" variant="ghost">Unfeature</Button>
                  ) : (
                    <Button data-testid={`admin-feature-${j.job_id}`} onClick={() => action(j.job_id, "feature")} size="sm" variant="ghost" className="text-yellow-300">Feature</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="p-3 rounded-lg bg-white/5 border border-white/10 text-center">
      <div className="text-2xl font-heading font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mt-1">{label}</div>
    </div>
  );
}

// ============ POINTS CONFIG ADMIN ============
function PointsAdminPanel() {
  const [cfg, setCfg] = useState(null);
  const [txns, setTxns] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/points/config").then(r => r.data),
        api.get("/points/history").then(r => r.data).catch(() => ({ items: [] })),
      ]);
      setCfg(c);
      setTxns(h.items || []);
    } catch { toast.error("Failed to load Credits config"); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const save = async () => {
    setSaving(true);
    try {
      const upd = {
        jobs_access_cost: Number(cfg.jobs_access_cost),
        partners_access_cost: Number(cfg.partners_access_cost),
        schools_access_cost: Number(cfg.schools_access_cost),
        school_view_credit_cost: Number(cfg.school_view_credit_cost),
        parent_student_portal_credit_fee: Number(cfg.parent_student_portal_credit_fee),
        headteacher_commission_pct: Number(cfg.headteacher_commission_pct),
        feed_view_threshold: Number(cfg.feed_view_threshold),
        feed_view_cost: Number(cfg.feed_view_cost),
        feed_view_free_initial: Number(cfg.feed_view_free_initial),
        // Phase 1 knobs
        friend_request_acceptance_credit_cost: Number(cfg.friend_request_acceptance_credit_cost),
        credit_purchase_referral_gen1_pct: Number(cfg.credit_purchase_referral_gen1_pct),
        credit_purchase_referral_gen2_pct: Number(cfg.credit_purchase_referral_gen2_pct),
        credit_purchase_referral_gen3_pct: Number(cfg.credit_purchase_referral_gen3_pct),
        // Phase 2 content thresholds
        video_qualification_pct: Number(cfg.video_qualification_pct),
        photo_qualification_seconds: Number(cfg.photo_qualification_seconds),
        text_qualification_seconds: Number(cfg.text_qualification_seconds),
        video_qualify_credit_cost: Number(cfg.video_qualify_credit_cost),
        photo_qualify_credit_cost: Number(cfg.photo_qualify_credit_cost),
        text_qualify_credit_cost: Number(cfg.text_qualify_credit_cost),
        // Phase 3 AI generation + CA + portals
        lesson_note_generation_credit_cost: Number(cfg.lesson_note_generation_credit_cost),
        question_generation_credit_cost: Number(cfg.question_generation_credit_cost),
        marking_scheme_generation_credit_cost: Number(cfg.marking_scheme_generation_credit_cost),
        headteacher_portal_open_credit_cost: Number(cfg.headteacher_portal_open_credit_cost),
        ca_weight_pct: Number(cfg.ca_weight_pct),
        paystack_enabled: !!cfg.paystack_enabled,
        paystack_public_key: cfg.paystack_public_key || "",
        paystack_currency: cfg.paystack_currency || "GHS",
        points_per_currency: Number(cfg.points_per_currency),
      };
      const fresh = await api.put("/admin/points/config", upd).then(r => r.data);
      setCfg(fresh);
      toast.success("Credits config saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-white/50">Loading…</div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });

  return (
    <div className="space-y-6">
      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Premium section fees (per day)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-white/50">
            Users see a confirmation prompt before points are deducted, and pay the fee once per calendar day per section.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PtField label="Jobs section fee" testId="pts-jobs" value={cfg.jobs_access_cost} onChange={v => set("jobs_access_cost", v)} />
            <PtField label="Find Partners fee" testId="pts-partners" value={cfg.partners_access_cost} onChange={v => set("partners_access_cost", v)} />
            <PtField label="Find School fee (daily)" testId="pts-schools" value={cfg.schools_access_cost} onChange={v => set("schools_access_cost", v)} />
            <PtField label="Individual school view fee" testId="pts-school-view" value={cfg.school_view_credit_cost} onChange={v => set("school_view_credit_cost", v)} />
            <PtField label="Parent portal fee (per Student ID / day)" testId="pts-parent-portal" value={cfg.parent_student_portal_credit_fee} onChange={v => set("parent_student_portal_credit_fee", v)} />
            <PtField label="Headteacher commission (% of parent fee)" testId="pts-ht-commission" value={cfg.headteacher_commission_pct} onChange={v => set("headteacher_commission_pct", v)} />
          </div>
          <p className="text-[10px] text-white/40 leading-relaxed">
            Browsing the Schools directory is always free. <b>Teachers &amp; Headteachers bypass the daily Schools Hub gate entirely.</b> The <b>Individual school view fee</b> is charged the first time a signed-in non-staff user opens a specific school profile per day. The <b>Parent portal fee</b> is charged the first time a signed-in non-staff user opens a specific Student ID per day, and the configured <b>Headteacher commission %</b> is instantly credited to that school's headteacher wallet.
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Feed viewing points</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-white/60 leading-relaxed">
            <b>Free grace window:</b> new users can watch the first <b>N</b> videos for free.
            After that, every <b>M</b> videos deducts <b>X</b> points automatically.
            If the user runs out of points, the feed is <b>locked</b> until they top up.
            Set any value to <b>0</b> to make feed viewing entirely free.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PtField label="Free videos for new users (N)" testId="pts-free-initial" value={cfg.feed_view_free_initial} onChange={v => set("feed_view_free_initial", v)} />
            <PtField label="Deduct after every M videos" testId="pts-threshold" value={cfg.feed_view_threshold} onChange={v => set("feed_view_threshold", v)} />
            <PtField label="Credits deducted (X)" testId="pts-cost" value={cfg.feed_view_cost} onChange={v => set("feed_view_cost", v)} />
          </div>
          <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-3 text-xs text-blue-200 leading-relaxed">
            <b>Live preview:</b>{" "}
            {(Number(cfg.feed_view_threshold) || 0) <= 0 || (Number(cfg.feed_view_cost) || 0) <= 0
              ? "Feed viewing is currently FREE."
              : `New users watch ${cfg.feed_view_free_initial || 0} videos free, then every ${cfg.feed_view_threshold} videos deducts ${cfg.feed_view_cost} point(s). Feed locks when they run out.`}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Friend requests &amp; credit-purchase referrals</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-white/50">
            Friend-request acceptance charges the receiver; sender earns 50%. Credit-purchase referrals share fiat back up the 3-generation chain.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <PtField label="Friend request accept (credits)" testId="pts-friend-request" value={cfg.friend_request_acceptance_credit_cost} onChange={v => set("friend_request_acceptance_credit_cost", v)} />
            <PtField label="Credit purchase Gen 1 %" testId="pts-cp-gen1" value={cfg.credit_purchase_referral_gen1_pct} onChange={v => set("credit_purchase_referral_gen1_pct", v)} step="0.1" />
            <PtField label="Credit purchase Gen 2 %" testId="pts-cp-gen2" value={cfg.credit_purchase_referral_gen2_pct} onChange={v => set("credit_purchase_referral_gen2_pct", v)} step="0.1" />
            <PtField label="Credit purchase Gen 3 %" testId="pts-cp-gen3" value={cfg.credit_purchase_referral_gen3_pct} onChange={v => set("credit_purchase_referral_gen3_pct", v)} step="0.1" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Content consumption thresholds (rewards + charges)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-white/50">
            A "qualified" consumption fires the creator reward and the optional viewer charge below. Anything shorter is completely free.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PtField label="Video qualify % of duration" testId="pts-video-pct" value={cfg.video_qualification_pct} onChange={v => set("video_qualification_pct", v)} step="0.5" />
            <PtField label="Photo qualify seconds" testId="pts-photo-secs" value={cfg.photo_qualification_seconds} onChange={v => set("photo_qualification_seconds", v)} />
            <PtField label="Text qualify seconds" testId="pts-text-secs" value={cfg.text_qualification_seconds} onChange={v => set("text_qualification_seconds", v)} />
            <PtField label="Video credit charge (per qualify)" testId="pts-video-cost" value={cfg.video_qualify_credit_cost} onChange={v => set("video_qualify_credit_cost", v)} />
            <PtField label="Photo credit charge (per qualify)" testId="pts-photo-cost" value={cfg.photo_qualify_credit_cost} onChange={v => set("photo_qualify_credit_cost", v)} />
            <PtField label="Text credit charge (per qualify)" testId="pts-text-cost" value={cfg.text_qualify_credit_cost} onChange={v => set("text_qualify_credit_cost", v)} />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Education AI + Continuous Assessment</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-white/50">
            Charges apply per successful AI generation. Continuous-Assessment weight determines how much of the final report card is derived from class tests (default 50%).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PtField label="Lesson notes AI (credits)" testId="pts-ai-lesson" value={cfg.lesson_note_generation_credit_cost} onChange={v => set("lesson_note_generation_credit_cost", v)} />
            <PtField label="Test / questions AI (credits)" testId="pts-ai-test" value={cfg.question_generation_credit_cost} onChange={v => set("question_generation_credit_cost", v)} />
            <PtField label="Marking scheme AI (credits)" testId="pts-ai-marking" value={cfg.marking_scheme_generation_credit_cost} onChange={v => set("marking_scheme_generation_credit_cost", v)} />
            <PtField label="Headteacher portal open (credits/day)" testId="pts-ht-open" value={cfg.headteacher_portal_open_credit_cost} onChange={v => set("headteacher_portal_open_credit_cost", v)} />
            <PtField label="Continuous Assessment weight %" testId="pts-ca-weight" value={cfg.ca_weight_pct} onChange={v => set("ca_weight_pct", v)} step="0.5" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Paystack (future point purchases)</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="pts-paystack-enabled" type="checkbox" checked={!!cfg.paystack_enabled} onChange={e => set("paystack_enabled", e.target.checked)} />
            Enable Paystack (requires <code className="text-yellow-300">PAYSTACK_SECRET_KEY</code> env var)
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <PtField label="Currency (e.g. GHS)" testId="pts-currency" value={cfg.paystack_currency} onChange={v => set("paystack_currency", v)} type="text" />
            <PtField label="Credits per unit currency" testId="pts-rate" value={cfg.points_per_currency} onChange={v => set("points_per_currency", v)} type="number" step="0.1" />
            <PtField label="Paystack public key" testId="pts-pubkey" value={cfg.paystack_public_key} onChange={v => set("paystack_public_key", v)} type="text" />
          </div>
          <p className="text-xs text-white/50">
            When enabled, users see a "Buy points" button that opens Paystack. On successful payment,
            points are credited automatically via <code>/api/paystack/verify</code>.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="pts-save" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Card className="bg-white/[0.03] border-white/10">
        <CardHeader><CardTitle className="font-heading">Recent transactions (yours)</CardTitle></CardHeader>
        <CardContent>
          {txns.length === 0 ? <p className="text-white/40 text-sm">No transactions yet.</p> :
            <div className="max-h-72 overflow-y-auto space-y-1 text-xs">
              {txns.slice(0, 50).map(t => (
                <div key={t.txn_id} className="flex justify-between p-2 rounded bg-white/5">
                  <span>{t.type} · {t.feature || t.meta?.feature || "-"} · {new Date(t.created_at).toLocaleString()}</span>
                  <span className={t.delta < 0 ? "text-red-300" : "text-emerald-300"}>{t.delta > 0 ? "+" : ""}{t.delta}</span>
                </div>
              ))}
            </div>}
        </CardContent>
      </Card>
    </div>
  );
}

function PtField({ label, value, onChange, testId, type = "number", step }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <input data-testid={testId} type={type} step={step || 1} value={value ?? ""} onChange={e => onChange(type === "number" ? e.target.value : e.target.value)}
             className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white" />
    </div>
  );
}



// ============================================================================
// Revenue Share Admin Panel — configure the 8-pillar Monthly Revenue Sharing
// Pool, exclude fraudulent users, preview net revenue, dry-run + finalize the
// monthly snapshot.
// ============================================================================
function RevenueShareAdminPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exclusions, setExclusions] = useState([]);
  const [newExclUser, setNewExclUser] = useState("");
  const [newExclReason, setNewExclReason] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, e, s] = await Promise.all([
        api.get("/revenue-share/config"),
        api.get("/revenue-share/exclusions"),
        api.get("/revenue-share/snapshots?limit=24"),
      ]);
      setCfg(c.data);
      setExclusions(e.data);
      setSnapshots(s.data);
    } catch { toast.error("Failed to load revenue-share config"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const previewMonth = async () => {
    try {
      const r = await api.get("/revenue-share/net-monthly-revenue", { params: { month } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveTopLevel = async (field, value) => {
    setSaving(true);
    try {
      const r = await api.put("/revenue-share/config", { [field]: value });
      setCfg(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveFactor = async (key, patch) => {
    setSaving(true);
    try {
      const r = await api.put("/revenue-share/config", { factors: { [key]: patch } });
      setCfg(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const exclude = async () => {
    if (!newExclUser.trim()) return;
    try {
      await api.post("/revenue-share/exclude-user",
        { user_id: newExclUser.trim(), reason: newExclReason || "" });
      setNewExclUser(""); setNewExclReason("");
      toast.success("User excluded from Revenue Share");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const unexclude = async (uid) => {
    if (!window.confirm(`Restore ${uid} to Revenue Share eligibility?`)) return;
    try {
      await api.delete(`/revenue-share/exclude-user/${uid}`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const finalize = async (dryRun) => {
    const label = dryRun ? "Run DRY-RUN finalization" : `FINALIZE ${month} — credit shares to users?`;
    if (!window.confirm(label)) return;
    setRunning(true);
    try {
      const r = await api.post("/revenue-share/finalize-month", { month, dry_run: dryRun });
      toast.success(`${dryRun ? "Dry" : "Live"} finalization complete. ${r.data.user_rows_count} user(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRunning(false); }
  };

  const viewSnapshot = async (sid) => {
    try {
      const r = await api.get(`/revenue-share/snapshots/${sid}`);
      setDetail(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!cfg) return <p className="text-white/50 text-sm">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="revenue-share-admin-panel">
      {/* Top-level thresholds */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading">Pool &amp; Eligibility</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumField label="Pool percentage (%)" testId="rs-pool-pct"
            value={cfg.pool_percentage}
            onChange={(v) => saveTopLevel("pool_percentage", Number(v))} />
          <NumField label="Min eligibility score" testId="rs-min-score"
            value={cfg.min_eligibility_score}
            onChange={(v) => saveTopLevel("min_eligibility_score", Number(v))} />
          <NumField label="Min withdrawal (Credits)" testId="rs-min-wd"
            value={cfg.min_withdrawal_credits}
            onChange={(v) => saveTopLevel("min_withdrawal_credits", Number(v))} />
          <NumField label="Min active days / month" testId="rs-min-days"
            value={cfg.min_active_days}
            onChange={(v) => saveTopLevel("min_active_days", Number(v))} />
          <div className="md:col-span-2 flex items-center gap-3 p-3 rounded bg-white/5 border border-white/10">
            <input type="checkbox" checked={!!cfg.legacy_creator_rewards_enabled}
              data-testid="rs-legacy-toggle"
              onChange={(e) => saveTopLevel("legacy_creator_rewards_enabled", e.target.checked)} />
            <div>
              <p className="text-sm">Legacy per-action creator rewards</p>
              <p className="text-[11px] text-white/50">
                Off (default) — creator payouts come from the Monthly Pool only.
                On — restore the immediate USD credit on every like/comment/etc.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 8 pillars */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading">Weighted Scoring — 8 Pillars</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="rs-factors">
          {Object.entries(cfg.factors || {}).map(([key, f]) => (
            <div key={key} className="p-3 rounded bg-white/5 border border-white/10" data-testid={`rs-factor-${key}`}>
              <div className="flex flex-col md:flex-row md:items-center gap-3">
                <label className="flex items-center gap-2 min-w-0 flex-1">
                  <input
                    type="checkbox"
                    checked={f.enabled}
                    data-testid={`rs-factor-${key}-enabled`}
                    onChange={(e) => saveFactor(key, { enabled: e.target.checked })}
                  />
                  <div className="min-w-0">
                    <p className="font-heading text-sm">{f.display_name}</p>
                    <p className="text-[11px] text-white/50">{f.description}</p>
                  </div>
                </label>
                <div className="flex items-center gap-2 w-full md:w-56">
                  <input
                    type="range" min="0" max="100" step="1"
                    value={f.weight}
                    data-testid={`rs-factor-${key}-weight-slider`}
                    onChange={(e) => setCfg({ ...cfg, factors: { ...cfg.factors,
                        [key]: { ...f, weight: Number(e.target.value) } } })}
                    onMouseUp={(e) => saveFactor(key, { weight: Number(e.target.value) })}
                    onTouchEnd={(e) => saveFactor(key, { weight: Number(e.target.value) })}
                    className="flex-1"
                  />
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={f.weight}
                    data-testid={`rs-factor-${key}-weight-input`}
                    onChange={(e) => setCfg({ ...cfg, factors: { ...cfg.factors,
                        [key]: { ...f, weight: Number(e.target.value) } } })}
                    onBlur={(e) => saveFactor(key, { weight: Number(e.target.value) })}
                    className="w-16 px-2 py-1 rounded bg-white/10 border border-white/10 text-white text-sm"
                  />
                  <span className="text-xs text-white/50">%</span>
                </div>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-white/40 pt-2">
            {saving ? "Saving…" : "Changes save on release/blur."}
          </p>
        </CardContent>
      </Card>

      {/* Exclusions */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading">Fraud &amp; Policy Exclusions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <input
              value={newExclUser}
              onChange={(e) => setNewExclUser(e.target.value)}
              placeholder="user_xxx"
              data-testid="rs-exclude-user-input"
              className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-white"
            />
            <input
              value={newExclReason}
              onChange={(e) => setNewExclReason(e.target.value)}
              placeholder="Reason"
              data-testid="rs-exclude-reason-input"
              className="flex-1 px-3 py-2 rounded bg-white/5 border border-white/10 text-white"
            />
            <Button data-testid="rs-exclude-btn" onClick={exclude}
              className="bg-red-600 hover:bg-red-700 min-h-[44px]">Exclude</Button>
          </div>
          <div className="space-y-2">
            {exclusions.length === 0 && <p className="text-white/50 text-sm">No exclusions.</p>}
            {exclusions.map((ex) => (
              <div key={ex.user_id} className="flex items-center justify-between p-2 rounded bg-white/5"
                   data-testid={`rs-exclusion-${ex.user_id}`}>
                <div>
                  <p className="text-sm font-medium">{ex.user_id}</p>
                  <p className="text-[11px] text-white/50">
                    {ex.reason || "no reason"} · by {ex.excluded_by} · {new Date(ex.excluded_at).toLocaleString()}
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => unexclude(ex.user_id)}
                  className="border-white/20 bg-transparent hover:bg-white/5">Restore</Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Finalization */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading">Monthly Pool Finalization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <input
              type="month" value={month} data-testid="rs-month-input"
              onChange={(e) => setMonth(e.target.value)}
              className="px-3 py-2 rounded bg-white/5 border border-white/10 text-white" />
            <Button data-testid="rs-preview-btn" onClick={previewMonth}
              className="bg-sky-600 hover:bg-sky-700 min-h-[44px]">Preview net revenue</Button>
            <Button data-testid="rs-dry-run-btn" onClick={() => finalize(true)} disabled={running}
              className="bg-amber-600 hover:bg-amber-700 min-h-[44px]">Dry-run finalize</Button>
            <Button data-testid="rs-finalize-btn" onClick={() => finalize(false)} disabled={running}
              className="bg-emerald-600 hover:bg-emerald-700 min-h-[44px]">Finalize &amp; credit shares</Button>
          </div>
          {preview && (
            <div className="p-3 rounded bg-white/5 border border-white/10 text-xs space-y-1"
                 data-testid="rs-preview-result">
              <p>Month <b>{preview.month}</b> — {preview.purchase_count} purchases, {preview.credits_sold} Credits sold</p>
              <p>Gross <b>${preview.gross_usd}</b> · Fees {preview.processing_fee_pct}% <b>${preview.processing_fees_usd}</b></p>
              <p>Net <b className="text-emerald-300">${preview.net_usd}</b>
                {" · "}Pool = <b className="text-yellow-300">${(preview.net_usd * cfg.pool_percentage / 100).toFixed(2)}</b>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Historical snapshots */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading">Historical Pool Snapshots</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {snapshots.length === 0 && <p className="text-white/50 text-sm">No snapshots yet.</p>}
          {snapshots.map((s) => (
            <div key={s.snapshot_id} className="flex items-center justify-between p-3 rounded bg-white/5"
                 data-testid={`rs-snapshot-${s.snapshot_id}`}>
              <div className="text-xs">
                <p className="font-heading text-sm">{s.month} {s.dry_run ? "· DRY" : ""}</p>
                <p className="text-white/50">
                  Pool <b>${s.pool_usd}</b> · {s.eligible_user_count} eligible · Total score {s.total_platform_score}
                </p>
                <p className="text-[10px] text-white/40">by {s.finalized_by} · {new Date(s.finalized_at).toLocaleString()}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => viewSnapshot(s.snapshot_id)}
                className="border-white/20 bg-transparent hover:bg-white/5">View</Button>
            </div>
          ))}
          {detail && (
            <div className="mt-4 p-3 rounded bg-white/5 border border-white/10" data-testid="rs-snapshot-detail">
              <div className="flex items-center justify-between mb-3">
                <p className="font-heading text-sm">
                  {detail.snapshot.month} — {detail.users.length} recipient(s), Pool ${detail.snapshot.pool_usd}
                </p>
                <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Close</Button>
              </div>
              <div className="max-h-96 overflow-y-auto space-y-1 text-xs">
                {detail.users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between p-2 rounded bg-white/[0.03]"
                       data-testid={`rs-snapshot-user-${u.user_id}`}>
                    <span className="font-mono">{u.user_id}</span>
                    <span>
                      <span className="text-white/60">score {u.weighted_score}</span>
                      {" · "}
                      <span className="text-white/60">{u.share_percentage}%</span>
                      {" · "}
                      <span className="text-emerald-300 font-bold">${u.confirmed_share_usd}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// A slim numeric field the panel uses for save-on-blur inputs.
function NumField({ label, value, onChange, testId }) {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <input
        data-testid={testId} type="number" value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => { if (Number(local) !== Number(value)) onChange(local); }}
        className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white"
      />
    </div>
  );
}

// ============================================================================
// Games Admin Panel — edit game name, entry cost, per-round Credit rewards,
// active flag, plus landing video explainer slide text.
// ============================================================================
function GamesAdminPanel() {
  const [games, setGames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [video, setVideo] = useState(null);
  const [videoDirty, setVideoDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const [g, v] = await Promise.all([
        api.get("/admin/games"),
        api.get("/landing/video-explainer"),
      ]);
      setGames(g.data); setVideo(v.data);
    } catch { toast.error("Failed to load games config"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patchGame = async (game_id, patch) => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/games/${game_id}`, patch);
      setGames((prev) => prev.map(g => g.game_id === game_id ? r.data.game : g));
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const patchVideo = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/landing/video-explainer",
        { title: video.title, subtitle: video.subtitle, slides: video.slides });
      setVideo(r.data);
      setVideoDirty(false);
      toast.success("Landing explainer saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8" data-testid="games-admin-panel">
      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3"><CardTitle className="font-heading">Games</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {games.length === 0 && <p className="text-white/50 text-sm">No games yet.</p>}
          {games.map((g) => (
            <div key={g.game_id} className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-3"
                 data-testid={`game-admin-${g.game_id}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Name</label>
                  <input
                    className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white"
                    defaultValue={g.name}
                    data-testid={`game-name-${g.game_id}`}
                    onBlur={(e) => e.target.value !== g.name && patchGame(g.game_id, { name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Entry cost (Credits)</label>
                  <input
                    type="number" min={0}
                    className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white"
                    defaultValue={g.entry_cost_credits}
                    data-testid={`game-entry-cost-${g.game_id}`}
                    onBlur={(e) => Number(e.target.value) !== g.entry_cost_credits &&
                                    patchGame(g.game_id, { entry_cost_credits: Number(e.target.value) })}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Description</label>
                  <textarea rows={2}
                    className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm"
                    defaultValue={g.description}
                    onBlur={(e) => e.target.value !== g.description && patchGame(g.game_id, { description: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">
                  Legal Championship — economics
                </p>
                <p className="text-[10px] text-white/40 mb-2">
                  Per-round entry fee, IRAC unlock cost, and per-tier payout schedule. Changes take effect immediately for all players.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    ["tournament_entry_credits", "Round entry"],
                    ["irac_unlock_credits", "IRAC unlock"],
                    ["pupil_perfect", "Pupil · perfect"],
                    ["pupil_per_correct", "Pupil · /correct"],
                    ["associate_perfect", "Associate · perfect"],
                    ["associate_per_correct", "Associate · /correct"],
                    ["partner_perfect", "Partner · perfect"],
                    ["partner_per_correct", "Partner · /correct"],
                    ["senior_advocate_perfect", "Sr Advocate · perfect"],
                    ["senior_advocate_per_correct", "Sr Advocate · /correct"],
                  ].map(([k, lbl]) => (
                    <div key={k}>
                      <label className="text-[10px] text-white/50">{lbl}</label>
                      <input type="number" min={0}
                        className="w-full mt-0.5 px-2 py-1.5 rounded bg-white/5 border border-white/10 text-white text-sm"
                        defaultValue={g.credit_rewards?.[k] ?? 0}
                        data-testid={`game-reward-${g.game_id}-${k}`}
                        onBlur={(e) => {
                          const next = { ...(g.credit_rewards || {}), [k]: Number(e.target.value) };
                          if (JSON.stringify(next) !== JSON.stringify(g.credit_rewards)) {
                            patchGame(g.game_id, { credit_rewards: next });
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                <label className="flex items-center gap-2 text-xs text-white/70">
                  <input type="checkbox" checked={!!g.is_active}
                    onChange={(e) => patchGame(g.game_id, { is_active: e.target.checked })} />
                  Active
                </label>
                <label className="flex items-center gap-2 text-xs text-white/70">
                  <input type="checkbox" checked={!!g.featured}
                    onChange={(e) => patchGame(g.game_id, { featured: e.target.checked })} />
                  Featured (top of list)
                </label>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader className="pb-3"><CardTitle className="font-heading">Landing Video Explainer</CardTitle></CardHeader>
        <CardContent className="space-y-4" data-testid="landing-video-admin">
          {!video && <p className="text-white/50 text-sm">Loading…</p>}
          {video && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Title</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white"
                    value={video.title || ""}
                    onChange={(e) => { setVideo({ ...video, title: e.target.value }); setVideoDirty(true); }}
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Subtitle</label>
                  <input className="w-full mt-1 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white"
                    value={video.subtitle || ""}
                    onChange={(e) => { setVideo({ ...video, subtitle: e.target.value }); setVideoDirty(true); }}
                  />
                </div>
              </div>
              <div className="space-y-3">
                {video.slides.map((slide, i) => (
                  <div key={i} className="p-3 rounded border border-white/10 bg-white/[0.03] space-y-2">
                    <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Slide {i + 1}</p>
                    <input className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white"
                      value={slide.headline || ""}
                      placeholder="Headline"
                      onChange={(e) => {
                        const next = [...video.slides];
                        next[i] = { ...next[i], headline: e.target.value };
                        setVideo({ ...video, slides: next }); setVideoDirty(true);
                      }}
                    />
                    <textarea rows={2} className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm"
                      value={slide.body || ""}
                      placeholder="Body"
                      onChange={(e) => {
                        const next = [...video.slides];
                        next[i] = { ...next[i], body: e.target.value };
                        setVideo({ ...video, slides: next }); setVideoDirty(true);
                      }}
                    />
                  </div>
                ))}
              </div>
              <Button onClick={patchVideo} disabled={!videoDirty || saving}
                data-testid="save-landing-video"
                className="bg-emerald-500 hover:bg-emerald-600 text-black font-heading font-bold">
                {saving ? "Saving…" : videoDirty ? "Save landing explainer" : "Saved"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


